/**
 * Похідна презентація колонок, отримана від automation замість метаданих документа.
 *
 * Навіщо. `label`, `description` і `widgetOptions` (зокрема списки значень дропдаунів) —
 * чисті функції від схеми: їх виводить automation і досі ЗАПИСУВАЛА в `_grist_Tables_column`
 * під час провізії. Через це кожна правка формулювання чи списку значень вимагала повного
 * проходу по всіх документах — а саме на цьому шляху сталася більшість інцидентів серпня.
 *
 * Тут ті самі значення беруться на читання. Модуль свідомо **не замінює** метадані, а лише
 * перекриває їх: поки презентація не завантажилась або не знає колонки, працює те, що вже
 * в документі. Тому крок безпечний сам по собі — запис можна прибирати окремо й пізніше.
 *
 * [provision-scope, етап 3]
 */
import { reportError } from "app/client/models/AppModel";

import { Observable } from "grainjs";

export interface ColumnPresentation {
  colId: string;
  label: string;
  description: string;
  widgetOptions: Record<string, any>;
  choiceType: "Choice" | "ChoiceList" | null;
}

/** Людська назва таблиці й набору — для переліків, де досі стояли сирі tableId. */
export interface TablePresentation {
  title: string;
  datasetTitle: string;
  groupId: number | null;
  /** Ключ запису за схемою (colId) — за ним імпорт оновлює наявні рядки. [[povtornyi-import-dodaie-dublikaty]] */
  primaryKey: string | null;
}

interface PresentationResponse {
  tables: Record<string, {
    title?: string;
    dataset_title?: string;
    group_id?: number;
    primary_key?: string | null;
    columns: ColumnPresentation[];
  }>;
}

// tableId -> colId -> презентація
let _byTable: Map<string, Map<string, ColumnPresentation>> | null = null;
// tableId (у нижньому регістрі: id у документі буває в іншому регістрі за схему) -> назви
let _tables: Map<string, TablePresentation> = new Map();

/**
 * Лічильник завантажень. Презентація приходить асинхронно, тож перелік, збудований до неї,
 * лишився б із сирими id — обчислення, що беруть назви, підписуються на нього.
 * [[import-tablytsia-pryznachennia-i-zistavlennia]]
 */
export const regionalPresentationVersion = Observable.create<number>(null, 0);
let _inFlight: Promise<void> | null = null;
let _attempts = 0;

const _MAX_ATTEMPTS = 3;

function _indexTables(payload: PresentationResponse): Map<string, TablePresentation> {
  const tables = new Map<string, TablePresentation>();
  for (const [tableId, table] of Object.entries(payload.tables || {})) {
    tables.set(tableId.toLowerCase(), {
      title: String(table.title || ""),
      datasetTitle: String(table.dataset_title || ""),
      groupId: typeof table.group_id === "number" ? table.group_id : null,
      primaryKey: table.primary_key ? String(table.primary_key) : null,
    });
  }
  return tables;
}

function _index(payload: PresentationResponse): Map<string, Map<string, ColumnPresentation>> {
  const byTable = new Map<string, Map<string, ColumnPresentation>>();
  for (const [tableId, table] of Object.entries(payload.tables || {})) {
    const byCol = new Map<string, ColumnPresentation>();
    for (const column of table.columns || []) {
      if (column?.colId) {
        byCol.set(column.colId, column);
      }
    }
    byTable.set(tableId, byCol);
  }
  return byTable;
}

/**
 * Завантажити презентацію один раз на сесію. Схеми однакові для всіх документів, тож і кеш
 * один. Помилку не роздмухуємо: без презентації інтерфейс працює на метаданих документа —
 * рівно так, як працював досі.
 */
export function loadRegionalPresentation(): Promise<void> {
  if (_byTable || _inFlight) {
    return _inFlight || Promise.resolve();
  }
  _inFlight = fetch("/api/regional/presentation", { credentials: "same-origin" })
    .then((resp) => (resp.ok ? resp.json() : Promise.reject(new Error(`HTTP ${resp.status}`))))
    .then((payload: PresentationResponse) => {
      _byTable = _index(payload);
      _tables = _indexTables(payload);
      regionalPresentationVersion.set(regionalPresentationVersion.get() + 1);
    })
    .catch((err) => {
      // Повторюємо кілька разів: мережевий збій не має назавжди лишати сесію на метаданих,
      // якщо запис із документа колись прибрали.
      _attempts += 1;
      if (_attempts < _MAX_ATTEMPTS) {
        setTimeout(() => { _inFlight = null; void loadRegionalPresentation(); }, 1000 * _attempts);
      } else {
        reportError(err);
      }
    })
    .then(() => { _inFlight = null; });
  return _inFlight;
}

export function getColumnPresentation(tableId: string | undefined, colId: string | undefined): ColumnPresentation | null {
  if (!_byTable || !tableId || !colId) {
    return null;
  }
  return _byTable.get(tableId)?.get(colId) || null;
}

export function getTablePresentation(tableId: string | undefined): TablePresentation | null {
  if (!tableId) {
    return null;
  }
  return _tables.get(tableId.toLowerCase()) || null;
}

/**
 * Підпис таблиці для людини: «39. Дані про видані дозволи… · permissions». Без презентації — id.
 * [[import-tablytsia-pryznachennia-i-zistavlennia]]
 */
export function regionalTableLabel(tableId: string): string {
  const table = getTablePresentation(tableId);
  if (!table || !table.datasetTitle) {
    return tableId;
  }
  const dataset = table.groupId !== null ? `${table.groupId}. ${table.datasetTitle}` : table.datasetTitle;
  return table.title ? `${dataset} · ${table.title}` : dataset;
}

/** Лише для тестів: скинути кеш між прогонами. */
export function _resetRegionalPresentationForTests(data?: PresentationResponse): void {
  _byTable = data ? _index(data) : null;
  _tables = data ? _indexTables(data) : new Map();
  _inFlight = null;
  _attempts = 0;
}
